import { useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import { deploymentProfile } from '../../config/deploymentProfile';

function ThemeSection({ showThemeControls = true }) {
  const { themeId, allThemes, resolved, setTheme, setOverride, addCustomTheme, validateThemeDefinition } = useTheme();
  const { addNotification } = useNotification();
  const importRef = useRef(null);
  const ui = resolved.ui || {};
  const baseTheme = allThemes[themeId] || resolved;

  const handleResetTextSizes = () => {
    setOverride('tickSize', baseTheme.tickSize ?? 13);
    setOverride('labelSize', baseTheme.labelSize ?? 15);
    setOverride('legendSize', baseTheme.legendSize ?? 12);
    setOverride('legendMarkerSize', baseTheme.legendMarkerSize ?? 14);
    setOverride('valueLabelSize', baseTheme.valueLabelSize ?? 12);
  };

  const handleDownload = () => {
    const blob = new Blob([JSON.stringify(resolved, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `coplot-theme-${(resolved.name || 'custom').toLowerCase().replace(/\s+/g, '-')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        const validationErrors = validateThemeDefinition(data);
        if (validationErrors.length > 0) {
          addNotification(`Invalid theme: ${validationErrors.join(', ')}`, 'error');
          return;
        }
        const id = data.id && !allThemes[data.id] ? data.id : `custom-${Date.now()}`;
        const name = data.name || `Custom ${new Date().toLocaleTimeString()}`;
        addCustomTheme({ ...data, id, name });
      } catch {
        addNotification('Could not parse JSON file.', 'error');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  return (
    <div className="border-b pb-3 mb-3 ui-border" style={{ borderBottomColor: ui.border }}>
      {showThemeControls && (
        <>
          <p className="text-xs mb-2" style={{ color: ui.textSecondary }}>Theme</p>
          <select
            value={themeId}
            onChange={(e) => setTheme(e.target.value)}
            className="w-full mb-2"
          >
            {Object.values(allThemes).map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </>
      )}

      {showThemeControls && deploymentProfile.allowThemeImport && (
        <div className="flex gap-1 mb-3">
          <button
            onClick={handleDownload}
            className="flex-1 text-xs border px-2 py-1 ui-hover ui-border"
            style={{ borderColor: ui.border }}
            title="Export current theme as JSON"
          >
            &darr; Export
          </button>
          <button
            onClick={() => importRef.current?.click()}
            className="flex-1 text-xs border px-2 py-1 ui-hover ui-border"
            style={{ borderColor: ui.border }}
            title="Import a theme JSON file"
          >
            &uarr; Import
          </button>
          <input
            ref={importRef}
            type="file"
            accept=".json,application/json"
            onChange={handleImport}
            className="hidden"
          />
        </div>
      )}

      <button
        onClick={handleResetTextSizes}
        className="w-full text-xs border px-2 py-1 ui-hover ui-border"
        style={{ borderColor: ui.borderSecondary, color: ui.textSecondary }}
        title="Reset all text sizes to theme defaults"
      >
        Reset text sizes
      </button>
    </div>
  );
}

export default ThemeSection;
